import i18n from '@/utils/i18n';
import React from 'react';
import { BORDER_RADIUS_SMALL } from '@/styles';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import {
  ItemListOptionSection,
  OrderGuideCollection,
} from '@/contexts/item-context-types';
import { SharePriceDropDownMenu } from './share-price-drop-down-menu';
import { Text, View } from 'react-native';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import {
  useGetOpenPriceQuotePDF,
  useGetPublicLinkPriceQuoteCSV,
} from '@/hooks/use-get-open-price-quote';
import { useKeyboardVisibility } from '@/hooks/use-keyboard-visibility';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useSalesRepMode } from '@/hooks/use-sales-rep-mode';
import { useToasts } from '@/contexts/toast-context';

const isOrderGuideEmpty = (
  orderGuideCollection: OrderGuideCollection | null,
  sections: ItemListOptionSection[],
): boolean => {
  if (!orderGuideCollection) {
    return true;
  }
  // find all partition ids, and filter the items that belong
  // to those partitions based on item metadata
  const orderGuidePartitionIds = new Set(
    orderGuideCollection.partitions.map((p) => p.uuid),
  );

  const orderGuideProducts = sections.filter((item) => {
    if (item && item.data) {
      const filteredOptions = item.data.filter((opt) => {
        const itemMetadata = opt.metadata;
        return (
          itemMetadata &&
          itemMetadata.orderGuidePartitionUUID &&
          orderGuidePartitionIds.has(itemMetadata.orderGuidePartitionUUID)
        );
      });
      return filteredOptions.length > 0;
    }
    return false;
  });

  return orderGuideProducts.length === 0;
};

export const SharePriceQuoteDropDownButton: React.FC = () => {
  const isPriceQuoteEnabled = useDynamicConfigValue(
    'price_quote_generation_enabled',
  );
  const { selectedCollection, sections } = useOrderGuideItemsContext();
  const { user, supplierEmployeeRole } = useAuthenticatedUserContext();
  const salesRepMode = useSalesRepMode();
  const { showErrorToast } = useToasts();
  const [styles] = useStyles();

  const isSupplierEmployee = Boolean(supplierEmployeeRole);

  const { loading, openPriceQuotePDF } = useGetOpenPriceQuotePDF();
  const { loading: exportCsvLoading, exportPriceQuoteCSV } =
    useGetPublicLinkPriceQuoteCSV();

  const menuOptions = [
    {
      label: (
        <Text style={styles.menuOptionText}>
          {i18n.t('generate_pdf')} {loading ? '...' : ''}
        </Text>
      ),
      onPress: async () => {
        try {
          // Fetch the price quote details and redirect to PMC link
          if (selectedCollection) {
            await openPriceQuotePDF(selectedCollection.uuid, user.uuid);
          } else {
            throw Error('No Order Guide has been defined');
          }
        } catch (e) {
          showErrorToast();
        }
      },
      trackingData: {
        name: `drawer_header.download_pdf_menu_option.press`,
        properties: {
          orderGuideRestaurantCollectionUUID: selectedCollection?.uuid,
        },
      },
    },
    {
      label: (
        <Text style={styles.menuOptionText}>
          {i18n.t('export_csv')} {exportCsvLoading ? '...' : ''}
        </Text>
      ),
      onPress: async () => {
        try {
          // Fetch the price quote details key and download the CSV
          if (selectedCollection) {
            await exportPriceQuoteCSV(selectedCollection.uuid, user.uuid);
          } else {
            throw Error('No Order Guide has been defined');
          }
        } catch (e) {
          showErrorToast();
        }
      },
      trackingData: {
        name: `drawer_header.export_csv_menu_option.press`,
        properties: {
          orderGuideRestaurantCollectionUUID: selectedCollection?.uuid,
        },
      },
    },
  ];

  return !isOrderGuideEmpty(selectedCollection, sections) &&
    isPriceQuoteEnabled &&
    isSupplierEmployee ? (
    <SharePriceDropDownMenu
      buttonTrackingData={{
        name: 'drawer_header.share_price_quote_dropdown',
        properties: {
          orderGuideRestaurantCollectionUUID: selectedCollection?.uuid,
        },
      }}
      menuOptions={menuOptions}
      salesRepMode={salesRepMode}
    />
  ) : null;
};

interface ButtonProps {
  onPress: () => void;
}

export const SharePriceQuoteButton: React.FC<ButtonProps> = ({ onPress }) => {
  const isPriceQuoteEnabled = useDynamicConfigValue(
    'price_quote_generation_enabled',
  );
  const { selectedCollection, sections } = useOrderGuideItemsContext();
  const { isSmallLayout } = useLayoutContext();
  const [styles] = useStyles();

  const isKeyboardVisible = useKeyboardVisibility();

  return !isOrderGuideEmpty(selectedCollection, sections) &&
    isPriceQuoteEnabled ? (
    !isKeyboardVisible && isSmallLayout ? (
      <View style={styles.buttonContainer}>
        <Button
          onPress={onPress}
          size="large"
          style={styles.button}
          trackingData={{
            name: 'share_price_quote_button',
            properties: {
              orderGuideRestaurantCollectionUUID: selectedCollection?.uuid,
            },
          }}
        >
          {i18n.t('create_price_list')}
        </Button>
      </View>
    ) : null
  ) : null;
};

const useStyles = createStyles((theme) => ({
  button: {
    borderRadius: BORDER_RADIUS_SMALL,
  },
  buttonContainer: {
    backgroundColor: theme.colors.transparent,
    bottom: 0,
    padding: 16,
    position: 'absolute',
    width: '100%',
  },
  menuOptionText: {
    fontFamily: theme.fonts.primary,
    fontSize: theme.fontSizes.small,
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
}));
